import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Clock, CreditCard, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { parseErrorMessage } from "@/lib/queryClient";
import {
  V3_STATUS_QUERY_KEY,
  confirmV3LifetimeOverage,
  confirmV3SavedCard,
  currentAppReturnPath,
  startV3CardSetup,
  startV3Plan,
  useV3Billing,
  v3StartLabel,
  type V3BillingStatusV3,
  type V3Banner,
  type V3BannerKind,
} from "@/hooks/use-v3-billing";

const DISMISS_KEY_PREFIX = "padpulse:v3-banner-dismissed:";

/** Only the trial reminder can be dismissed (for this browser session). */
export function isDismissibleV3Banner(kind: V3BannerKind): boolean {
  return kind === "trial_ending";
}

function readDismissed(kind: V3BannerKind): boolean {
  if (!isDismissibleV3Banner(kind)) return false;
  try {
    return sessionStorage.getItem(`${DISMISS_KEY_PREFIX}${kind}`) === "1";
  } catch {
    return false;
  }
}

function actionLabel(banner: V3Banner, status: V3BillingStatusV3 | null): string {
  switch (banner.action) {
    case "add_card":
      return banner.kind === "past_due" ? "Update card" : "Add a card";
    case "confirm_overage":
      return "Confirm billing";
    case "confirm_saved_card":
      return status?.savedCard?.last4 ? `Keep card ending ${status.savedCard.last4}` : "Keep my saved card";
    case "start":
      return v3StartLabel(status);
  }
}

/**
 * App-shell banner for v3 per-property accounts (trial ending, paused, past
 * due, lifetime over-allowance, saved-card start). Renders nothing for legacy
 * accounts or when the server returns no banner. Every action leaves for a
 * Stripe-hosted page or starts the plan server-side; the webhook confirms.
 */
export function V3BillingBanner() {
  const status = useV3Billing();
  const banner = status?.banner ?? null;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dismissedKind, setDismissedKind] = useState<V3BannerKind | null>(null);

  const actionMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (b: V3Banner) => {
      if (b.action === "confirm_saved_card") {
        await confirmV3SavedCard();
        return { redirected: false };
      }
      if (b.action === "add_card") {
        await startV3CardSetup(currentAppReturnPath());
        return { redirected: true };
      }
      if (b.action === "confirm_overage") {
        const data = await confirmV3LifetimeOverage();
        return { redirected: !!data.url };
      }
      const data = await startV3Plan();
      return { redirected: !!data.url };
    },
    onSuccess: (result, b) => {
      if (result.redirected) return;
      queryClient.invalidateQueries({ queryKey: V3_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
      toast({
        title: b.action === "confirm_overage" ? "Billing confirmed" : "You're all set",
        description: b.action === "confirm_overage"
          ? "Your extra properties are now billed monthly."
          : "Your PadPulse plan is active.",
      });
    },
    onError: (err) => {
      toast({
        title: "Something went wrong",
        description: parseErrorMessage(err, "We couldn't open billing just now. Please try again."),
        variant: "destructive",
      });
    },
  });

  if (!banner) return null;
  if (dismissedKind === banner.kind || readDismissed(banner.kind)) return null;

  const dismissible = isDismissibleV3Banner(banner.kind);
  const urgent = banner.kind === "paused" || banner.kind === "past_due";
  const Icon = urgent ? AlertTriangle : banner.kind === "trial_ending" ? Clock : CreditCard;
  const tone = urgent
    ? "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
    : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100";

  return (
    <div
      role={urgent ? "alert" : "status"}
      className={`shrink-0 border-b px-4 py-2 text-sm ${tone}`}
      data-testid={`banner-v3-${banner.kind}`}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span data-testid="text-v3-banner-message">{banner.message}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-brand hover:bg-brand-hover text-white"
            disabled={actionMutation.isPending}
            onClick={() => actionMutation.mutate(banner)}
            data-testid="button-v3-banner-action"
          >
            {actionLabel(banner, status)}
          </Button>
          {banner.action === "confirm_saved_card" && (
            <Button
              size="sm"
              variant="outline"
              disabled={actionMutation.isPending}
              onClick={() => actionMutation.mutate({ ...banner, action: "add_card" })}
              data-testid="button-v3-banner-new-card"
            >
              Add a new card
            </Button>
          )}
          {dismissible && (
            <button
              type="button"
              className="rounded p-1 opacity-70 hover:opacity-100"
              aria-label="Dismiss"
              onClick={() => {
                try {
                  sessionStorage.setItem(`${DISMISS_KEY_PREFIX}${banner.kind}`, "1");
                } catch {}
                setDismissedKind(banner.kind);
              }}
              data-testid="button-v3-banner-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small "Founding member" / "Lifetime" pill for the header account area. */
export function V3LifetimeBadge() {
  const status = useV3Billing();
  if (!status?.lifetime) return null;
  return (
    <span
      className="hidden sm:inline-flex items-center rounded-full border border-brand/30 bg-brand-subtle px-2.5 py-1 text-xs font-medium text-brand"
      data-testid="badge-v3-lifetime"
      title={`Covers up to ${status.lifetime.allowance} properties`}
    >
      {status.lifetime.label}
    </span>
  );
}
